type CaptureReader = {
  cancel: () => Promise<unknown> | unknown;
};

type CaptureTrack = {
  stop: () => void;
  addEventListener?: (name: "ended", listener: () => void, options?: {once?: boolean}) => void;
};

type CaptureStream = {
  getTracks: () => CaptureTrack[];
};

type GifEncoder = {
  on: (name: string, listener: (...args: never[]) => void) => void;
  render: () => void;
  abort: () => void;
};

type CapturedFrames = {
  current: unknown[];
};

export const MEDIA_RECORDER_OPTIONS = {
  mimeType: "video/webm;codecs=vp8",
  videoBitsPerSecond: 2500000,
} as const;

/**
 * Release capture resources without turning cleanup-only failures into export
 * failures. Every track is attempted even if an earlier cleanup step fails.
 */
export async function cleanupMediaCapture(
  reader: CaptureReader | null,
  stream: CaptureStream | null
): Promise<unknown[]> {
  const errors: unknown[] = [];

  if (reader) {
    try {
      await reader.cancel();
    } catch (error) {
      errors.push(error);
    }
  }

  if (stream) {
    let tracks: CaptureTrack[] = [];
    try {
      tracks = stream.getTracks();
    } catch (error) {
      errors.push(error);
    }

    for (const track of tracks) {
      try {
        track.stop();
      } catch (error) {
        errors.push(error);
      }
    }
  }

  return errors;
}

export function getMediaRecorderError(event: unknown): Error {
  if (event && typeof event === "object" && "error" in event) {
    const error = event.error;
    if (error instanceof Error) return error;
    if (typeof error === "string" && error) return new Error(error);
    if (
      error &&
      typeof error === "object" &&
      "message" in error &&
      typeof error.message === "string"
    ) {
      const normalized = new Error(error.message, {cause: error});
      if ("name" in error && typeof error.name === "string") normalized.name = error.name;
      return normalized;
    }
  }

  return new Error("MediaRecorder failed");
}

export function observeCaptureCancellation(track: CaptureTrack, onCancelled: () => void): void {
  track.addEventListener?.("ended", onCancelled, {once: true});
}

export function finishGifCapture(
  frames: CapturedFrames,
  setGifLoading: (loading: boolean) => void
): void {
  frames.current = [];
  setGifLoading(false);
}

export function renderGifWithWatchdog(
  gif: GifEncoder,
  {
    stallTimeoutMs = 30_000,
    onProgress,
  }: {stallTimeoutMs?: number; onProgress?: (progress: number) => void} = {}
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    let settled = false;
    let timer: ReturnType<typeof setTimeout>;

    const armWatchdog = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        reject(new Error("GIF encoder stalled"));
        try {
          gif.abort();
        } catch {
          // The rejection above is authoritative; abort is best-effort cleanup.
        }
      }, stallTimeoutMs);
    };
    const fail = (error: Error) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      reject(error);
    };

    gif.on("finished", (blob: Blob) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(blob);
    });
    gif.on("progress", (progress: number) => {
      armWatchdog();
      onProgress?.(progress);
    });
    gif.on("abort", () => fail(new Error("GIF encoding aborted")));

    armWatchdog();
    try {
      gif.render();
    } catch (error) {
      fail(error instanceof Error ? error : new Error(String(error)));
    }
  });
}
